import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { Policy } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { EvaluatedAddon } from '../plan-picker/plan-picker.component';
import { AddonDependencyStatus, evaluateDependencyStatuses, cascadeDeselect } from '../../utils/addon-dependency.utils';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-addon-picker',
  templateUrl: './addon-picker.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: AddonPickerComponent,
    },
  ],
})
export class AddonPickerComponent implements ControlValueAccessor, OnChanges {
  @PropertyListener('addons') addons$ = new BehaviorSubject<{ planId: string; addons: EvaluatedAddon[] } | null>(null);
  @Input() addons: { planId: string; addons: EvaluatedAddon[] } | null = null;

  @PropertyListener('selectedTerm') selectedTerm$ = new BehaviorSubject<Policy.PolicyTerm | null>(null);
  @Input() selectedTerm: Policy.PolicyTerm | null = null;

  @Input() selectedAddons: string[] | null = null;
  @PropertyListener('selectedAddons') protected selectedAddons$ = new BehaviorSubject<string[] | null>(
    this.selectedAddons
  );

  @Input() disabled = false;

  private touched = false;

  private _onChange?: (selectedAddons: string[]) => void;
  private _onTouched?: () => void;

  /** Map of addonId → dependency status, recomputed on selection/addon changes */
  dependencyStatusMap = new Map<string, AddonDependencyStatus>();

  ngOnChanges(changes: SimpleChanges) {
    if (changes['addons'] || changes['selectedAddons']) {
      this.refreshDependencyStatuses();
    }
  }

  /** Recompute dependency statuses based on current selection */
  private refreshDependencyStatuses() {
    const allAddons = (this.addons?.addons ?? []).map(ea => ea.addon);
    const selected = this.selectedAddons ?? [];
    const statuses = evaluateDependencyStatuses(allAddons, selected);
    this.dependencyStatusMap = new Map(statuses.map(s => [s.addonId, s]));
  }

  /** Check if an addon's dependencies are met */
  isDependencyMet(addonId: string): boolean {
    return this.dependencyStatusMap.get(addonId)?.dependenciesMet ?? true;
  }

  /** Get the "Requires all/any of: X, Y" label for a given addon */
  getDependencyHint(addonId: string): string | null {
    const status = this.dependencyStatusMap.get(addonId);
    if (!status?.requiredAddonTitles?.length) return null;
    const op = status.operator === 'OR' ? 'any' : 'all';
    return `Requires ${op} of: ${status.requiredAddonTitles.join(', ')}`;
  }

  addonPrices$ = combineLatest([this.selectedTerm$, this.addons$]).pipe(
    map(([term, addons]) => {
      const hasOneTime = term?.paymentSchedules.some(schedule => schedule.installments === 1) ?? false;
      const installmentSchedules = term?.paymentSchedules.filter(schedule => schedule.installments > 1) ?? [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const addonPrices: Record<string, any> = {};

      if (!addons?.addons.length) return addonPrices;

      for (const addon of addons.addons) {
        const installments = [];
        for (const schedule of installmentSchedules) {
          const price = this.calculatePrice(addon.price, schedule.installments, schedule.rate);
          installments.push({ price, count: schedule.installments });
        }
        addonPrices[addon.addonId] = {
          price: hasOneTime ? addon.price : installments.length ? installments[0].price : addon.price,
          hasOneTime,
          installments,
          duration: term?.duration ?? 0,
        };
      }

      return addonPrices;
    })
  );

  calculatePrice(amount: number, installments: number, rate: number) {
    let rateIncrease = 0;
    if (rate !== 0) {
      rateIncrease = (amount * rate) / 100;
    }
    return Math.floor((amount + rateIncrease) / installments);
  }

  selectAddon(addonId: string) {
    if (this.selectedAddons?.includes(addonId)) return; //nothing to update
    this.markAsTouched();
    const addonIds: string[] =
      this.selectedAddons && this.selectedAddons.length ? [...this.selectedAddons, addonId] : [addonId];
    this.selectedAddons = addonIds;
    this.refreshDependencyStatuses();
    this._onChange?.(this.selectedAddons);
  }

  deselectAddon(addonId: string) {
    if (this.selectedAddons && this.selectedAddons.length && this.selectedAddons.includes(addonId)) {
      this.markAsTouched();
      const withoutTarget = this.selectedAddons.filter(id => id !== addonId);
      // Cascade: remove any addons whose dependencies are no longer met
      const allAddons = (this.addons?.addons ?? []).map(ea => ea.addon);
      this.selectedAddons = cascadeDeselect(allAddons, withoutTarget);
      this.refreshDependencyStatuses();
      this._onChange?.(this.selectedAddons);
    }
  }

  writeValue(selectedAddons: string[]): void {
    this.selectedAddons = selectedAddons;
    this.refreshDependencyStatuses();
  }

  registerOnChange(onChange: (selectedAddons: string[]) => void): void {
    this._onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this._onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this._onTouched?.();
      this.touched = true;
    }
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  isSelected(addonId: string) {
    const isSelected = JSON.stringify(this.selectedAddons).includes(addonId);
    return isSelected ? addonId : null;
  }
}
